import React from "react";
import styled from "styled-components";
import { pxToRem } from "styles/utils";

export interface SegmentedControlOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface SegmentedControlProps {
  value: string;
  options: SegmentedControlOption[];
  onChange: (value: string) => void;
  ariaLabel?: string;
  className?: string;
}

const Control = styled.div`
  display: inline-flex;
  align-items: stretch;
  max-width: 100%;
  overflow-x: auto;
  border: ${pxToRem(1.25)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${pxToRem(12.5)};
  background: ${({ theme }) => theme.colors.white};
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const OptionButton = styled.button<{ $active: boolean }>`
  flex: 0 0 auto;
  height: ${pxToRem(47.5)};
  padding: 0 ${pxToRem(17.5)};
  border: 0;
  border-right: ${pxToRem(1.25)} solid ${({ theme }) => theme.borders.divider};
  background: ${({ $active, theme }) => ($active ? theme.colors.brandSoft : "transparent")};
  color: ${({ $active, theme }) => ($active ? theme.colors.brand : theme.colors.inkSoft)};
  font: inherit;
  font-size: ${pxToRem(16)};
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:last-child {
    border-right: 0;
  }

  &:hover:not(:disabled) {
    background: ${({ $active, theme }) => ($active ? theme.colors.brandSoft : theme.colors.inkSurface)};
  }

  &:focus-visible {
    position: relative;
    z-index: 1;
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(-2)};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const SegmentedControl: React.FC<SegmentedControlProps> = ({
  value,
  options,
  onChange,
  ariaLabel = "Переключатель",
  className,
}) => (
  <Control role="group" aria-label={ariaLabel} className={className}>
    {options.map((option) => {
      const isActive = value === option.value;
      return (
        <OptionButton
          key={option.value}
          type="button"
          $active={isActive}
          aria-pressed={isActive}
          disabled={option.disabled}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </OptionButton>
      );
    })}
  </Control>
);

export default SegmentedControl;
